import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { cx } from './cx';

export type ToastTone = 'ok' | 'danger' | 'info';
type Notify = (message: string, options?: { tone?: ToastTone; duration?: number }) => void;
interface ToastItem { id: number; message: string; tone: ToastTone }

const ToastContext = createContext<{ notify: Notify }>({ notify: () => {} });
export const useToast = () => useContext(ToastContext);

/** Wrap the app once. `notify(message, { tone: 'ok' | 'danger' | 'info' })`. Announced politely to screen readers. */
export function ToastProvider({ children }: { children?: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const notify = useCallback<Notify>((message, { tone = 'info', duration = 4000 } = {}) => {
    const id = ++seq.current;
    setToasts((t) => [...t, { id, message, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), duration);
  }, []);

  const value = useMemo(() => ({ notify }), [notify]);
  const toneClass: Record<ToastTone, string> = { ok: 'bg-ok text-white', danger: 'bg-danger text-white', info: 'bg-ink text-white' };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[1100] flex flex-col gap-2" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={cx('pointer-events-auto rounded-lg px-4 py-2.5 text-sm font-medium shadow-pop', toneClass[t.tone])}>
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
